import _ from 'lodash'
import { CollapseTransition } from '../transition'
import Checkbox from '../checkbox'
import { getComponentsByName } from '../../core/utils/KVdom'
import { NODE_ID } from './treeHelper'
import treeMixin, { COMPONENT_NAME } from './treeMixin'

export default {
  name: 'KTreeNode',
  mixins: [treeMixin],
  inject: ['tree'],
  props: {
    data: {
      type: Object,
      required: true,
      // `{}`
      default() {
        return {}
      },
    },
    level: {
      type: Number,
      default: 0,
    },
    isLastChild: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // 子节点部分选中
      indeterminate: false,
      // 展开子节点
      expanded: this.tree.expandAll,
    }
  },
  computed: {
    hasChildren() {
      return this.data.children && this.data.children.length > 0
    },
    selected() {
      return this.tree.selectedData && this.tree.selectedData[NODE_ID] === this.data[NODE_ID]
    },
    showCheckbox() {
      return this.tree.checkable
    },
    checked() {
      return !!_.find(this.tree.checkedData, (item) => item[NODE_ID] === this.data[NODE_ID])
    },
    active() {
      return this.tree.activeIndex === this.data[NODE_ID]
    },
    disabled() {
      return this.data.disabled
    },
    nodeKey() {
      return this.data[this.tree.nodeKey]
    },
  },
  watch: {
    // 默认展开的节点
    'tree.expandedKeys': {
      handler(val) {
        if (this.tree.expandAll) {
          return
        }
        if (!val || val.length === 0 || !this.nodeKey || val.indexOf(this.nodeKey) === -1) {
          this.expanded = false
          return
        }
        this.expandNode()
      },
      immediate: true,
    },
    // 默认check的节点
    'tree.checkedKeys': {
      handler(val) {
        if (!val || val.length === 0 || !this.nodeKey || val.indexOf(this.nodeKey) === -1) {
          this.uncheckNode(this)
          this.traverse()
          return
        }
        this.checkNode(this)
        this.traverse()
        // 展开
        if (this.tree.expandAll && this.tree.defaultExpandChecked) {
          this.expandNode()
        }
        // 防止设置默认值时往外emit事件
        this.tree.triggerCheckBySelf = true
      },
      immediate: true,
    },
    // 默认select的节点
    'tree.selectedKey': {
      handler(val) {
        // checkable 模式不能设置默认select的节点
        if (!val || !this.nodeKey || val !== this.nodeKey || this.tree.checkable) {
          return
        }
        this.tree.selectedData = this.data
        // 展开
        if (!this.tree.expandAll && this.tree.defaultExpandSelected) {
          this.expandNode()
        }
        // 防止设置默认值时往外emit事件
        this.tree.triggerSelectBySelf = true
      },
      immediate: true,
    },
    // 默认展开所有
    'tree.expandAll': {
      handler(val) {
        // 避免影响已展开的node
        this.expanded = val
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * 获取父节点
     * @param node
     * @returns {any}
     */
    getParentTreeNode(node) {
      return (node.$parent && node.$parent.$options.name === COMPONENT_NAME) ? node.$parent : null
    },

    /**
     * 展开当前节点
     */
    expandNode() {
      this.expanded = true
      this.expandParents(this)
    },

    /**
     * 展开父节点
     * @param node
     */
    expandParents(node) {
      const parent = this.getParentTreeNode(node)
      if (!parent) {
        return
      }
      parent.expanded = true
      // 继续展开其父节点
      this.expandParents(parent)
    },

    /**
     * Node点击事件
     */
    handleTreeNodeClick() {
      if (this.disabled) {
        return
      }
      // checkable 和 selectable 互斥，优先checkable
      if (this.tree.checkable) {
        // 当前激活的Node
        this.tree.activeIndex = this.data[NODE_ID]
        // 点击时选中/反选节点
        if (this.tree.checkOnSelect) {
          if (this.checked) {
            this.uncheckNode(this)
          } else {
            this.checkNode(this)
          }
          this.traverse()
          this.triggerChecked()
        }
      } else {
        this.tree.selectedData = this.data
      }
    },

    /**
     * 展开收起事件
     * @param e
     */
    handleToggleClick(e) {
      this.expanded = !this.expanded
      // 防止触发 node click，导致行被选中
      e.stopPropagation()
    },

    /**
     * 遍历并同步其父子节点的选中状态
     * @param force 强制遍历 children / parents
     */
    traverse(force = '') {
      // 非严格模式：遍历并改变父子节点
      if (!this.tree.checkStrictly || force) {
        // 不管是选中还是反选，都要退出 indeterminate 状态
        this.indeterminate = false
        if (!this.tree.checkStrictly || force === 'parents') {
          // 遍历其父节点
          this.traverseParents(this)
        }
        if (!this.tree.checkStrictly || force === 'children') {
          // 遍历其子节点
          this.traverseChildren(this)
        }
      }
    },

    handleCheckedChange() {
      this.traverse()
      this.triggerChecked()
    },

    triggerChecked() {
      this.tree.triggerChecked({
        data: this.data,
        node: this,
        checked: this.checked,
      })
    },

    /**
     * 追加选中节点
     * @param node
     */
    checkNode(node) {
      this.tree.checkedData.push(node.data)
      // 去重
      this.tree.checkedData = _.uniqBy(this.tree.checkedData, NODE_ID)
    },

    /**
     * 移除选中节点
     * @param node
     */
    uncheckNode(node) {
      const index = _.findIndex(this.tree.checkedData, (item) => item[NODE_ID] === node.data[NODE_ID])
      if (index !== -1) {
        this.tree.checkedData.splice(index, 1)
      }
    },

    /**
     * 检查节点影响到的父节点
     * @param node
     */
    traverseParents(node) {
      const parent = this.getParentTreeNode(node)
      if (!parent) {
        return
      }
      const siblingNodes = getComponentsByName(parent.$children, COMPONENT_NAME)
      const hasChecked = _.some(siblingNodes, ['checked', true])
      const hasUnchecked = _.some(siblingNodes, ['checked', false])
      if (!this.tree.checkStrictly) {
        const hasIndeterminate = _.some(siblingNodes, ['indeterminate', true])
        parent.indeterminate = (hasChecked && hasUnchecked) || hasIndeterminate
      }
      const check = !hasUnchecked
      // is different
      if (parent.checked !== check) {
        if (check) {
          this.checkNode(parent)
        } else {
          this.uncheckNode(parent)
        }
      }
      // continue
      this.traverseParents(parent)
    },

    /**
     * 检查节点影响到的子节点
     * @param node
     */
    traverseChildren(node) {
      const children = this.getChildren(node)
      _.each(children, (child) => {
        // is different
        if (child.checked !== this.checked) {
          if (this.checked) {
            this.checkNode(child)
          } else {
            this.uncheckNode(child)
          }
        }
        // 将子节点设置为非indeterminate
        child.indeterminate = false
        // continue
        this.traverseChildren(child)
      })
    },

    /**
     * 展开收起
     * @returns {*}
     */
    renderToggle() {
      let icon = null

      if (this.hasChildren) {
        if (this.tree.showLine) {
          icon = <k-icon icon={this.expanded ? 'minus' : 'plus'}></k-icon>
        } else {
          icon = <k-icon icon="caret-right"></k-icon>
        }
      }

      const css = {
        'km-tree-node__toggle': true,
        // 带连接线时不需要 transition
        'km-tree-node__toggle--expanded': !this.tree.showLine && this.expanded,
      }

      const events = {
        click: this.handleToggleClick,
      }

      const data = {
        class: css,
        on: events,
      }

      return (
        <span {...data}>
          {icon}
        </span>
      )
    },

    /**
     * 选择框
     */
    renderCheckbox() {
      if (!this.showCheckbox) {
        return null
      }
      return <Checkbox
        class="km-tree-node__checkbox"
        small
        value={this.data}
        v-model={this.tree.checkedData}
        disabled={this.disabled}
        indeterminate={this.indeterminate}
        nativeOnClick={(e) => { e.stopPropagation() }}
        onChange={this.handleCheckedChange}
      />
    },

    /**
     * 节点内容
     * @returns {*}
     */
    renderContent() {
      const style = {
        'padding-left': `${(this.level - 1) * this.tree.indentUnitWidth}px`,
      }

      const css = {
        'km-tree-node__content': true,
      }

      const events = {
        click: this.handleTreeNodeClick,
      }

      const data = {
        style,
        class: css,
        on: events,
      }

      return (
        <div {...data}>
          {this.renderToggle()}
          {this.renderCheckbox()}
          {this.renderLabel()}
        </div>
      )
    },

    /**
     * 节点label
     * @returns {*}
     */
    renderLabel() {
      return this.$scopedSlots.default
        ? this.$scopedSlots.default({
          node: this,
          data: this.data,
        })
        : <span className="km-tree-node__label">{this.data.label}</span>
    },

    /**
     * line
     * @returns {*}
     */
    renderLine() {
      if (this.isLastChild) {
        return null
      }

      const style = {
        'left': `${(this.level - 1) * this.tree.indentUnitWidth + 30}px`,
      }

      const css = {
        'km-tree-node__line': true,
      }

      const data = {
        style,
        class: css,
      }
      return <div {...data}></div>
    },

    /**
     * 子节点
     * @returns {*}
     */
    renderChildren() {
      if (!this.hasChildren) {
        return null
      }
      const children = this.renderTreeNode(this.data.children)

      return (
        <CollapseTransition>
          <div
            class="km-tree-node__children"
            v-show={this.expanded}
          >
            {this.tree.showLine ? this.renderLine() : null}
            {children}
          </div>
        </CollapseTransition>
      )
    },
  },

  render() {
    const css = {
      'km-tree-node': true,
      ['km-tree-node__level--' + this.level]: true,
      'km-tree-node--selected': this.selected,
      // 高亮最后点击的Node，暂时不启用，给人的感觉不够干脆
      // 'km-tree-node--active': this.active,
      // select模式，禁用状态则禁用整个node
      'km-tree-node--disabled': !this.tree.checkable && this.disabled,
      // 'km-tree-node--show-line': this.tree.showLine,
    }

    const data = {
      class: css,
    }

    return (
      <div {...data}>
        {this.renderContent()}
        {this.renderChildren()}
      </div>
    )
  },
}
